import { useEffect, useRef } from "react";

/**
 * Dynamically loads Google Fonts based on font names and weights.
 * Injects a <link> element into the document head.
 */
export function useGoogleFonts(
	fonts: { name: string; weight?: string }[],
) {
	const linkRef = useRef<HTMLLinkElement | null>(null);

	useEffect(() => {
		const validFonts = fonts.filter((f) => f.name);
		if (validFonts.length === 0) return;

		const families = validFonts
			.map((f) => {
				const family = f.name.replace(/ /g, "+");
				const weights = [
					...new Set((f.weight || "400;500;600;700").split(";")),
				]
					.sort()
					.join(";");
				return `family=${family}:wght@${weights}`;
			})
			.join("&");

		const url = `https://fonts.googleapis.com/css2?${families}&display=swap`;

		// Avoid duplicate links
		if (linkRef.current?.getAttribute("href") === url) return;

		// Remove previous link if font changed
		if (linkRef.current) {
			document.head.removeChild(linkRef.current);
		}

		const link = document.createElement("link");
		link.rel = "stylesheet";
		link.href = url;
		document.head.appendChild(link);
		linkRef.current = link;

		return () => {
			if (linkRef.current) {
				document.head.removeChild(linkRef.current);
				linkRef.current = null;
			}
		};
	}, [fonts.map((f) => `${f.name}:${f.weight}`).join(",")]);
}
